/**
 * Floating panel overlays — renders undocked panels as draggable windows.
 *
 * Panel metadata, colors, and body components live in panelRegistry.tsx
 * so they can be shared with Sidebar.
 */

import { useEffect } from "react";
import { DraggablePanel } from "./DraggablePanel.tsx";
import {
  FLOATING_PANEL_IDS,
  MANNEQUIN_ONLY_PANELS,
  PANEL_ALLOWED_MODES,
  useEditorMode,
  useFloatingPanelStore,
} from "./floatingPanelStore.ts";
import {
  PANEL_COLORS,
  PANEL_META,
  PanelBody,
  useMannequinObjectId,
  usePanelApplicabilities,
} from "./panelRegistry.tsx";
import { getPanelBodyStyle } from "./panelSurface.tsx";
import { COLOR_ACCENT, RADIUS_XS, Z_FLOATING } from "./tokens.ts";

const PANEL_BODY = getPanelBodyStyle({
  padding: "10px 14px",
  overflowY: "auto",
});

// ---------------------------------------------------------------------------
// Floating panel overlays — rendered in the overlay area of App.tsx
// Only renders panels that are NOT docked.
// ---------------------------------------------------------------------------

export function FloatingPanelOverlays() {
  const mode = useEditorMode();
  const openPanels = useFloatingPanelStore((s) => s.openPanels);
  const dockedPanels = useFloatingPanelStore((s) => s.dockedPanels);
  const close = useFloatingPanelStore((s) => s.close);
  const selectedPanelKey = useFloatingPanelStore((s) => s.selectedPanelKey);
  const selectPanel = useFloatingPanelStore((s) => s.selectPanel);
  const clearSelection = useFloatingPanelStore((s) => s.clearSelection);
  const mannequinObjectId = useMannequinObjectId();
  const panelApplicable = usePanelApplicabilities();

  // Escape closes the selected panel (capture phase — runs before global handler).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      const state = useFloatingPanelStore.getState();
      if (state.selectedPanelKey === null) return;
      e.preventDefault();
      e.stopPropagation();
      state.closeSelectedPanel?.();
    };
    window.addEventListener("keydown", handler, true);
    return () => window.removeEventListener("keydown", handler, true);
  }, []);

  // Click outside any panel clears selection.
  useEffect(() => {
    if (selectedPanelKey === null) return;
    const handler = (e: PointerEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest("[data-draggable-panel]")) {
        clearSelection();
      }
    };
    window.addEventListener("pointerdown", handler);
    return () => window.removeEventListener("pointerdown", handler);
  }, [selectedPanelKey, clearSelection]);

  return (
    <>
      {FLOATING_PANEL_IDS.map((id) => {
        // Skip panels that live in the dock
        if (dockedPanels.has(id)) return null;

        const allowed =
          PANEL_ALLOWED_MODES[id].includes(mode) &&
          (!MANNEQUIN_ONLY_PANELS.has(id) || mannequinObjectId !== null) &&
          panelApplicable[id];
        const isOpen = allowed && openPanels.has(id);
        if (!isOpen) return null;

        const meta = PANEL_META[id];
        const colors = PANEL_COLORS[id];
        const panelKey = `fp-${id}`;
        const closeFn = () => close(id);

        return (
          <DraggablePanel
            key={id}
            id={panelKey}
            defaultPosition={meta.defaultPosition}
            zIndex={Z_FLOATING}
            collapsible
            selected={selectedPanelKey === panelKey}
            selectionColor={COLOR_ACCENT}
            onSelect={() => selectPanel(panelKey, closeFn)}
            header={
              <>
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 20,
                    height: 20,
                    borderRadius: RADIUS_XS,
                    background: colors.bgIdle,
                    color: colors.fg,
                    flexShrink: 0,
                  }}
                >
                  {meta.icon}
                </span>
                {meta.label}
              </>
            }
            onClose={closeFn}
            style={{ width: meta.width }}
            bodyStyle={PANEL_BODY}
          >
            <PanelBody panelId={id} mode={mode} />
          </DraggablePanel>
        );
      })}
    </>
  );
}
